import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Group, Loader, Paper, Text, ThemeIcon, Tooltip, Transition } from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconCloudUpload,
  IconRefresh,
  IconX,
} from '@tabler/icons-react';
import { saveStatusVisibility } from './saveStatusIndicatorState';

export type SaveStatus = 'saved' | 'unsaved' | 'saving' | 'invalid' | 'error';

const STATUS_DETAILS: Record<
  SaveStatus,
  { label: string; color: string; icon: typeof IconCheck }
> = {
  saved: { label: 'Changes saved', color: 'green', icon: IconCheck },
  unsaved: { label: 'Unsaved changes', color: 'yellow', icon: IconAlertCircle },
  saving: { label: 'Saving changes…', color: 'blue', icon: IconCloudUpload },
  invalid: { label: 'Fix highlighted fields to save', color: 'red', icon: IconAlertCircle },
  error: { label: 'Couldn’t save changes', color: 'red', icon: IconX },
};

export interface SaveStatusIndicatorProps {
  status: SaveStatus;
  onRetry?: () => void;
}

/**
 * Transient autosave feedback pinned to the bottom of the section being saved.
 *
 * Stays hidden while the user types, appears while the save is in flight, then
 * fades out on success. Failures remain visible until the next successful save.
 *
 * Render as the last child of the saved section's `Stack`; it sticks to the
 * bottom of that section's scroll area rather than the viewport.
 */
export function SaveStatusIndicator({ status, onRetry }: SaveStatusIndicatorProps): JSX.Element {
  const [visible, setVisible] = useState(false);
  const previousStatusRef = useRef<SaveStatus | undefined>(undefined);
  const details = STATUS_DETAILS[status];
  const StatusIcon = details.icon;

  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = status;
    const next = saveStatusVisibility(status, previousStatus);
    setVisible(next.visible);
    if (!next.dwellMs) {
      return undefined;
    }
    const timer = setTimeout(() => setVisible(false), next.dwellMs);
    return () => clearTimeout(timer);
  }, [status]);

  return (
    <Transition mounted={visible} transition="fade-up" duration={160} exitDuration={400}>
      {(transitionStyle) => (
        <Paper
          withBorder
          shadow="md"
          radius="md"
          p="sm"
          role="status"
          aria-live="polite"
          style={{
            ...transitionStyle,
            position: 'sticky',
            bottom: 'var(--mantine-spacing-md)',
            alignSelf: 'flex-start',
            zIndex: 2,
          }}
        >
          <Group gap="xs" wrap="nowrap">
            <ThemeIcon color={details.color} variant="light" radius="xl" size="md">
              {status === 'saving' ? (
                <Loader color={details.color} size="xs" />
              ) : (
                <StatusIcon size={16} stroke={1.8} aria-hidden />
              )}
            </ThemeIcon>
            <Text size="sm" fw={600} c="gray.8">
              {details.label}
            </Text>
            {status === 'error' && onRetry && (
              <Tooltip label="Retry saving">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={onRetry}
                  aria-label="Retry saving changes"
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Paper>
      )}
    </Transition>
  );
}
